'use client';

import { create } from 'zustand';
import { uploadFileToS3Api } from '@/shared/apis/asset-upload/put-file-to-s3';
import { uploadThumbnailToS3Api } from '@/shared/apis/asset-upload/put-thumbnail-to-s3';
import {
  patchProjectUploadCompleteApi,
  patchProjectThumbnailCompleteApi,
} from '@/shared/apis/project';
import { useSourceFileStore } from '@/shared/store/source-file-store';
import { classifyUploadFailure } from '@/shared/utils/upload-error-classifier';
import { reportClassifiedUploadFailure } from '@/shared/utils/report-upload-failure';
import { MULTIPART_THRESHOLD } from '@/shared/apis/asset-upload/smart-upload';
import {
  initiateMultipartUploadForContentApi,
  completeMultipartUploadApi,
  abortMultipartUploadApi,
} from '@/shared/apis/asset-upload/multipart-upload-api';
import { uploadMultipartToS3 } from '@/shared/apis/asset-upload/upload-multipart-to-s3';

/**
 * 업로드 파일 정보
 */
export interface UploadFileInfo {
  /** 파일 고유 ID (UI에서 진행률 추적용) */
  fileId: string;
  /** 서버에서 생성된 Content ID (업로드 실패 시 상태 업데이트용) */
  contentId?: number;
  /** 콘텐츠 타입 (로컬 파일 또는 URL 콘텐츠) */
  contentType: 'LOCAL_FILE' | 'URL_CONTENT';
  /** 업로드할 파일 (로컬 파일인 경우) */
  file?: File;
  /**
   * 썸네일 원본 이미지 URL (URL 콘텐츠인 경우)
   * - YouTube 등 외부 서비스에서 제공하는 썸네일 이미지 URL
   * - 예: https://i.ytimg.com/vi/xxx/maxresdefault.jpg
   * - 이 URL에서 이미지를 fetch하여 Blob으로 변환 후 S3에 업로드
   */
  thumbnailUrl?: string;
  /** S3 presigned URL (로컬 파일인 경우) */
  presignedUrl?: string;
  /**
   * 썸네일 S3 업로드용 presigned URL
   * - 로컬 파일: 비디오에서 추출한 썸네일을 업로드할 S3 URL
   * - URL 콘텐츠: thumbnailUrl에서 fetch한 이미지를 업로드할 S3 URL
   * - thumbnailUrl (원본) → fetch → Blob → thumbnailPresignedUrl (S3) → 업로드
   */
  thumbnailPresignedUrl?: string;
  /** 영상 길이 (ms) - 썸네일 추출 위치 계산용 */
  duration?: number;
  /** 업로드 진행률 (0-100) */
  progress: number;
  /** 업로드 상태 */
  status: 'pending' | 'uploading' | 'completed' | 'error';
  /** 에러 메시지 */
  errorMessage?: string;
}

/**
 * 프로젝트 생성 소스 타입
 */
export type ProjectCreationSource = 'lts' | 'mts' | null;

/**
 * Upload Store 상태 인터페이스
 */
interface UploadState {
  // 상태
  files: UploadFileInfo[];
  isUploading: boolean;
  /** 프로젝트 생성 중인지 여부 (LTS/MTS 공통) */
  isCreatingProject: boolean;
  /** 프로젝트 생성 소스 (어디서 생성 중인지) */
  projectCreationSource: ProjectCreationSource;

  // 파일 관리 액션
  addFiles: (files: UploadFileInfo[]) => void;
  updateProgress: (fileId: string, progress: number) => void;
  setStatus: (
    fileId: string,
    status: UploadFileInfo['status'],
    errorMessage?: string,
  ) => void;

  // 프로젝트 생성 상태 관리
  startProjectCreation: (source: ProjectCreationSource) => void;
  endProjectCreation: () => void;

  // 업로드 실행
  uploadFiles: () => Promise<void>;
  uploadSingleFile: (fileInfo: UploadFileInfo) => Promise<void>;
  completeUpload: (projectId: number, contentIds: number[]) => Promise<void>;
  /** 썸네일 업로드 완료 (YouTube URL용, Content 상태 변경 없음) */
  thumbnailComplete: (projectId: number, contentIds: number[]) => Promise<void>;

  // 리셋
  reset: () => void;
}

const uploadFileToS3 = async (
  file: File,
  presignedUrl: string,
  onProgress?: (progress: number) => void,
): Promise<void> => {
  // 프로젝트 생성 플로우 = PROJECTS 버킷 (initContentForUpload 경로).
  await uploadFileToS3Api({
    file,
    presignedUrl,
    onProgress,
    diagnosticsTarget: 'PROJECTS',
  });
};

const uploadThumbnailToS3 = async (
  thumbnailBlob: Blob,
  presignedUrl: string,
): Promise<void> => {
  await uploadThumbnailToS3Api({ thumbnailBlob, presignedUrl });
};

/**
 * 공통 Upload Store
 *
 * MTS, LTS 등 모든 feature에서 공통으로 사용하는 업로드 상태 관리
 * - S3 파일 업로드
 * - 썸네일 추출 및 업로드
 * - 업로드 진행률 추적
 * - 업로드 완료 API 호출
 */
export const useProjectUploadStore = create<UploadState>((set, get) => ({
  files: [],
  isUploading: false,
  isCreatingProject: false,
  projectCreationSource: null,

  /**
   * 프로젝트 생성 시작
   */
  startProjectCreation: (source) =>
    set({ isCreatingProject: true, projectCreationSource: source }),

  /**
   * 프로젝트 생성 완료
   */
  endProjectCreation: () =>
    set({ isCreatingProject: false, projectCreationSource: null }),

  /**
   * 업로드할 파일 목록 설정
   */
  addFiles: (files) => set({ files }),

  /**
   * 파일 업로드 진행률 업데이트
   */
  updateProgress: (fileId, progress) =>
    set((state) => ({
      files: state.files.map((f) =>
        f.fileId === fileId ? { ...f, progress } : f,
      ),
    })),

  /**
   * 파일 업로드 상태 업데이트
   */
  setStatus: (fileId, status, errorMessage) =>
    set((state) => ({
      files: state.files.map((f) =>
        f.fileId === fileId ? { ...f, status, errorMessage } : f,
      ),
    })),

  /**
   * 단일 파일 업로드 (파일 + 썸네일)
   */
  uploadSingleFile: async (fileInfo: UploadFileInfo) => {
    const { updateProgress, setStatus } = get();

    setStatus(fileInfo.fileId, 'uploading');

    try {
      // 로컬 파일 업로드
      if (fileInfo.contentType === 'LOCAL_FILE' && fileInfo.file) {
        const onProgressSync = (progress: number) => {
          updateProgress(fileInfo.fileId, progress);
          useSourceFileStore
            .getState()
            .updateProgress(fileInfo.fileId, progress);
        };

        if (fileInfo.file.size >= MULTIPART_THRESHOLD && fileInfo.contentId) {
          // 대용량 파일: 멀티파트 업로드 (기존 Content 활용)
          const initResponse = await initiateMultipartUploadForContentApi(
            fileInfo.contentId,
          );
          const {
            uploadId,
            partSize,
            partPresignedUrls,
            thumbnailPreSignedUrl,
          } = initResponse.data;

          try {
            // MP4/MOV fast-start 변환 (Streaming 방식, 파일 크기 제한 없음)
            let uploadFile = fileInfo.file;
            try {
              const { applyFastStart } =
                await import('@/shared/utils/mp4-faststart');
              uploadFile = await applyFastStart(fileInfo.file);
            } catch (e) {
              console.warn(
                '[mp4-faststart] fast-start 처리 실패, 원본 파일로 업로드:',
                e,
              );
            }

            const completedParts = await uploadMultipartToS3({
              file: uploadFile,
              partPresignedUrls,
              partSize,
              onProgress: onProgressSync,
            });

            // 썸네일 업로드 (멀티파트 initiate에서 받은 presigned URL 사용)
            const thumbUrl =
              thumbnailPreSignedUrl ?? fileInfo.thumbnailPresignedUrl;
            if (thumbUrl) {
              try {
                if (fileInfo.file.type.startsWith('video/')) {
                  const { extractThumbnailFromVideo } =
                    await import('@/shared/utils/video-thumbnail');
                  const durationInSeconds = (fileInfo.duration || 0) / 1000;
                  const thumbnailBlob = await extractThumbnailFromVideo(
                    fileInfo.file,
                    durationInSeconds,
                  );
                  if (thumbnailBlob) {
                    await uploadThumbnailToS3(thumbnailBlob, thumbUrl);
                  }
                } else if (fileInfo.file.type.startsWith('image/')) {
                  await uploadThumbnailToS3(fileInfo.file, thumbUrl);
                }
              } catch (thumbnailError) {
                // FE 썸네일 업로드 실패는 silent — 메인 업로드는 성공이고
                // server (AI media-process) 폴백 생성이 후속 처리한다.
                // 추적용으로 console.warn 가시화 (메인 catch 의 텔레메트리와 분리).
                console.warn(
                  '[Upload] 썸네일 업로드 실패 (silent — server fallback 처리):',
                  {
                    error: thumbnailError,
                    contentId: fileInfo.contentId,
                    fileName: fileInfo.file?.name,
                    path: 'shared/upload-store/multipart',
                  },
                );
              }
            }

            await completeMultipartUploadApi({
              contentId: fileInfo.contentId,
              uploadId,
              parts: completedParts,
            });
          } catch (error) {
            await abortMultipartUploadApi({
              contentId: fileInfo.contentId!,
              uploadId,
            });
            throw error;
          }
        } else if (fileInfo.presignedUrl) {
          // 소용량 파일: 기존 단일 PUT 업로드
          await uploadFileToS3(
            fileInfo.file,
            fileInfo.presignedUrl,
            onProgressSync,
          );

          // 썸네일 업로드
          if (fileInfo.thumbnailPresignedUrl) {
            try {
              if (fileInfo.file.type.startsWith('video/')) {
                const { extractThumbnailFromVideo } =
                  await import('@/shared/utils/video-thumbnail');
                const durationInSeconds = (fileInfo.duration || 0) / 1000;
                const thumbnailBlob = await extractThumbnailFromVideo(
                  fileInfo.file,
                  durationInSeconds,
                );
                if (thumbnailBlob) {
                  await uploadThumbnailToS3(
                    thumbnailBlob,
                    fileInfo.thumbnailPresignedUrl,
                  );
                }
              } else if (fileInfo.file.type.startsWith('image/')) {
                await uploadThumbnailToS3(
                  fileInfo.file,
                  fileInfo.thumbnailPresignedUrl,
                );
              }
            } catch (thumbnailError) {
              // FE 썸네일 업로드 실패는 silent — 메인 업로드는 성공이고
              // server (AI media-process) 폴백 생성이 후속 처리한다.
              console.warn(
                '[Upload] 썸네일 업로드 실패 (silent — server fallback 처리):',
                {
                  error: thumbnailError,
                  contentId: fileInfo.contentId,
                  fileName: fileInfo.file?.name,
                  path: 'shared/upload-store/single-put',
                },
              );
            }
          }
        }
      }

      // URL 콘텐츠 썸네일 업로드
      // thumbnailUrl (원본 이미지 URL) → fetch → Blob → thumbnailPresignedUrl (S3) → 업로드
      if (
        fileInfo.contentType === 'URL_CONTENT' &&
        fileInfo.thumbnailUrl &&
        fileInfo.thumbnailPresignedUrl
      ) {
        try {
          const { fetchThumbnailFromUrl } =
            await import('@/shared/utils/video-thumbnail');
          // 1. thumbnailUrl에서 이미지 fetch하여 Blob으로 변환
          const thumbnailBlob = await fetchThumbnailFromUrl(
            fileInfo.thumbnailUrl,
          );
          if (thumbnailBlob) {
            // 2. Blob을 thumbnailPresignedUrl로 S3에 업로드
            await uploadThumbnailToS3(
              thumbnailBlob,
              fileInfo.thumbnailPresignedUrl,
            );
            updateProgress(fileInfo.fileId, 100);
            // source-file-store에도 progress 동기화
            useSourceFileStore.getState().updateProgress(fileInfo.fileId, 100);
          }
        } catch (thumbnailError) {
          // URL 썸네일 실패해도 프로젝트 생성은 계속 진행 (silent fallback).
          // server fallback 으로 후속 처리되거나 사용자가 별도 업로드 가능.
          console.warn(
            '[Upload] URL 썸네일 업로드 실패 (silent — fallback 처리):',
            {
              error: thumbnailError,
              contentId: fileInfo.contentId,
              thumbnailUrl: fileInfo.thumbnailUrl,
              path: 'shared/upload-store/url-content',
            },
          );
        }
      }

      setStatus(fileInfo.fileId, 'completed');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed';
      setStatus(fileInfo.fileId, 'error', errorMessage);

      // 분류기 결과를 텔레메트리 errorReason 으로 전송 (S3UploadError 4 reason 외 + 메타데이터 단계 reason 도 커버).
      // 헬퍼가 mimeType / fileExtension / errorCode / httpStatus / userAgent / traceId 등 메타 자동 채움.
      const fileName = fileInfo.file?.name ?? fileInfo.fileId;
      const classified = classifyUploadFailure(error, fileName);
      reportClassifiedUploadFailure(
        error,
        // fileInfo.file 이 없을 수 있으므로 안전하게 minimal 객체 구성 (예: 진행 중 fileId 만 가진 경우)
        fileInfo.file ?? { name: fileName },
        classified,
        {
          contentId: fileInfo.contentId,
          uploadPath: 'shared/upload-store',
        },
      );

      throw error;
    }
  },

  /**
   * 모든 파일 업로드 (최대 5개 동시)
   */
  uploadFiles: async () => {
    const { files, uploadSingleFile } = get();
    set({ isUploading: true });

    const MAX_CONCURRENT = 5;

    try {
      // 동시 실행 수를 제한하는 방식으로 업로드
      const executing = new Set<Promise<void>>();
      for (const fileInfo of files) {
        const p = uploadSingleFile(fileInfo).finally(() => executing.delete(p));
        executing.add(p);
        if (executing.size >= MAX_CONCURRENT) {
          await Promise.race(executing);
        }
      }
      await Promise.all(executing);
    } finally {
      set({ isUploading: false });
    }
  },

  /**
   * 업로드 완료 API 호출
   */
  completeUpload: async (projectId, contentIds) => {
    await patchProjectUploadCompleteApi({ projectId, contentIds });
  },

  /**
   * 썸네일 업로드 완료 API 호출 (YouTube URL용)
   *
   * - Content 상태 변경 없음 (YouTube 다운로드 완료 전까지 NOT_READY 유지)
   * - upload-complete API와 분리하여 의도 명확화
   */
  thumbnailComplete: async (projectId, contentIds) => {
    await patchProjectThumbnailCompleteApi({ projectId, contentIds });
  },

  /**
   * 상태 초기화
   */
  reset: () =>
    set({
      files: [],
      isUploading: false,
      isCreatingProject: false,
      projectCreationSource: null,
    }),
}));

/**
 * 업로드 파일 정보 생성 헬퍼
 *
 * @param params.fileId - 파일 고유 ID
 * @param params.contentType - 콘텐츠 타입 ('LOCAL_FILE' | 'URL_CONTENT')
 * @param params.file - 로컬 파일 (LOCAL_FILE인 경우)
 * @param params.presignedUrl - S3 파일 업로드용 presigned URL (LOCAL_FILE인 경우)
 * @param params.thumbnailUrl - 썸네일 원본 이미지 URL (URL_CONTENT인 경우, 예: YouTube 썸네일 URL)
 * @param params.thumbnailPresignedUrl - 썸네일 S3 업로드용 presigned URL
 * @param params.duration - 영상 길이 (ms)
 */
export const createUploadFileInfo = (params: {
  fileId: string;
  contentId?: number;
  contentType: 'LOCAL_FILE' | 'URL_CONTENT';
  file?: File;
  presignedUrl?: string;
  thumbnailUrl?: string;
  thumbnailPresignedUrl?: string;
  duration?: number;
}): UploadFileInfo => ({
  ...params,
  progress: 0,
  status: 'pending',
});
